/**
 * iter-367 — Client-facing AI Creator panel.
 *
 * A sandboxed variant of the admin AI Task Creator. Same 3 modes
 * (Single/Multi, Full Project BOQ, Upload from File) but:
 *   - No worksheet selector — client has no access to admin BOQs.
 *   - Preview cards show ONE consumer-facing rate column (client_rate).
 *   - No "Add to BOQ" button — preview-only cost exploration.
 *   - Backend endpoint (/client-ai-creator/preview) never emits vendor_rate,
 *     admin markup or pricing_context.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sparkles, Wand2, Building2, Image as FileIcon, Upload, Loader2, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientAICreatorPanel() {
  const [mode, setMode] = useState('single');
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [instruction, setInstruction] = useState('');
  const [budget, setBudget] = useState('');
  const [area, setArea] = useState('');
  const [tier, setTier] = useState('mid');
  const [projectType, setProjectType] = useState('residential');
  const [fileBase64, setFileBase64] = useState('');
  const [fileMime, setFileMime] = useState('');
  const [fileName, setFileName] = useState('');
  // iter-386u-f — task #5: usage / credit gating.
  const [usage, setUsage] = useState({ used: 0, free_quota: 2, extra_credits: 0, remaining: 2, blocked: false });
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/client-ai-creator/usage`, { headers: { Authorization: `Bearer ${token}` } });
        setUsage(r.data);
      } catch { /* silent */ }
    })();
  }, [token]);

  const refreshUsage = async () => {
    try {
      const r = await axios.get(`${API}/client-ai-creator/usage`, { headers: { Authorization: `Bearer ${token}` } });
      setUsage(r.data);
    } catch { /* silent */ }
  };

  const requestCredits = async () => {
    setRequesting(true);
    try {
      const r = await axios.post(`${API}/client-ai-creator/request-credits`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRequested(true);
      toast.success(r.data?.already_pending ? 'Your request is already awaiting admin approval' : 'Credits requested — admin has been notified');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send request');
    } finally {
      setRequesting(false);
    }
  };

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isImage = (f.type || '').startsWith('image/');
    const isPdf = (f.type || '').toLowerCase() === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
    if (!isImage && !isPdf) { toast.error('Only image or PDF files supported'); return; }
    if (f.size > 12 * 1024 * 1024) { toast.error('File must be under 12 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setFileBase64(String(reader.result || ''));
      setFileName(f.name);
      setFileMime(isPdf ? 'application/pdf' : (f.type || 'image/*'));
    };
    reader.readAsDataURL(f);
  };

  const runPreview = async () => {
    if (usage.blocked) {
      toast.error('You have used all your AI Creator credits. Please request more.');
      return;
    }
    setLoading(true);
    setPreviews([]);
    try {
      const body = { mode };
      if (mode === 'single') {
        if (!instruction.trim()) { toast.error('Enter a description first'); setLoading(false); return; }
        body.instruction = instruction.trim();
      } else if (mode === 'full') {
        body.instruction = instruction.trim() || undefined;
        body.budget = Number(budget) || undefined;
        body.area_sqft = Number(area) || undefined;
        body.quality_tier = tier;
        body.project_type = projectType;
      } else {
        if (!fileBase64) { toast.error('Upload a file first'); setLoading(false); return; }
        body.file_base64 = fileBase64;
        body.file_mime = fileMime;
        body.file_name = fileName;
        body.instruction = instruction.trim() || undefined;
      }
      const r = await axios.post(`${API}/client-ai-creator/preview`, body, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 120000,
      });
      setPreviews(r.data?.previews || []);
      if (!(r.data?.previews || []).length) toast.warning('No items generated');
      else toast.success(`Generated ${r.data.previews.length} items`);
      refreshUsage();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Free AI Creator quota exhausted. Tap "Request Credits" below.');
        refreshUsage();
      } else {
        toast.error(err.response?.data?.detail || 'AI request failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const totalEstimate = previews.reduce((s, p) => s + (Number(p.estimated_total) || 0), 0);
  const copySummary = async () => {
    const lines = previews.map((p) =>
      `${p.task_title} · ${p.quantity} ${p.unit} · ₹${Math.round(p.client_rate).toLocaleString('en-IN')}/${p.unit} = ₹${Math.round(p.estimated_total).toLocaleString('en-IN')}`);
    const total = `\nEstimated Total: ₹${Math.round(totalEstimate).toLocaleString('en-IN')}`;
    try {
      await navigator.clipboard.writeText(lines.join('\n') + total);
      toast.success('Summary copied to clipboard');
    } catch { toast.error('Could not copy — please select and copy manually'); }
  };

  return (
    <div className="rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-purple-50 p-4" data-testid="client-ai-creator-panel">
      <div className="mb-3">
        <h3 className="font-semibold text-stone-900 flex items-center gap-2 text-base">
          <Sparkles size={16} className="text-violet-600" />
          AI Creator
        </h3>
        <p className="text-xs text-stone-600 mt-0.5">Explore rough costs for your interior work. Estimates are indicative and not linked to any admin quotation.</p>
        {/* iter-386u-f — task #5: usage + request-credits row */}
        <div className="flex items-center gap-2 mt-2">
          <span className="text-[10px] uppercase font-bold tracking-wider text-violet-700 bg-violet-100 px-2 py-0.5 rounded" data-testid="ai-creator-usage-chip">
            {usage.used} / {usage.free_quota + usage.extra_credits} used · {usage.remaining} left
          </span>
          {usage.blocked && !requested && (
            <button
              type="button"
              onClick={requestCredits}
              disabled={requesting}
              className="text-[11px] px-2 py-1 rounded font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
              data-testid="ai-creator-request-credits-btn"
            >
              {requesting ? 'Sending…' : 'Request Credits'}
            </button>
          )}
          {requested && (
            <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
              Credits requested — awaiting admin
            </span>
          )}
        </div>
      </div>
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="bg-white border">
          <TabsTrigger value="single" data-testid="client-ai-tab-single" className="text-xs">
            <Wand2 size={12} className="mr-1" /> Single / Multi Item
          </TabsTrigger>
          <TabsTrigger value="full" data-testid="client-ai-tab-full" className="text-xs">
            <Building2 size={12} className="mr-1" /> Full Project BOQ
          </TabsTrigger>
          <TabsTrigger value="file" data-testid="client-ai-tab-file" className="text-xs">
            <FileIcon size={12} className="mr-1" /> Upload from File
          </TabsTrigger>
        </TabsList>

        <TabsContent value="single" className="mt-3 space-y-2">
          <Label className="text-xs">Describe what you need</Label>
          <Textarea
            rows={2}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="e.g. 8ft x 6ft wardrobe with mirror + TV unit 6x4 for a 3BHK living room"
            className="bg-white text-stone-900"
            data-testid="client-ai-single-instruction"
          />
        </TabsContent>

        <TabsContent value="full" className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div>
            <Label className="text-[10px] uppercase text-stone-500">Budget (₹)</Label>
            <Input type="number" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. 800000" data-testid="client-ai-full-budget" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-stone-500">Area (sq.ft)</Label>
            <Input type="number" value={area} onChange={(e) => setArea(e.target.value)} placeholder="e.g. 1200" data-testid="client-ai-full-area" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-stone-500">Quality Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger className="bg-white" data-testid="client-ai-full-tier"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="budget">Budget</SelectItem>
                <SelectItem value="mid">Mid</SelectItem>
                <SelectItem value="premium">Premium</SelectItem>
                <SelectItem value="luxury">Luxury</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-stone-500">Project Type</Label>
            <Select value={projectType} onValueChange={setProjectType}>
              <SelectTrigger className="bg-white" data-testid="client-ai-full-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="hospitality">Hospitality</SelectItem>
                <SelectItem value="retail">Retail</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-4">
            <Label className="text-[10px] uppercase text-stone-500">Extra Notes (Optional)</Label>
            <Input value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="e.g. Focus on kitchen and living areas" data-testid="client-ai-full-notes" />
          </div>
        </TabsContent>

        <TabsContent value="file" className="mt-3 space-y-2">
          <label htmlFor="client-ai-file-input" className="flex items-center gap-2 cursor-pointer bg-white text-stone-700 border border-dashed border-stone-300 hover:border-violet-400 hover:bg-violet-50 rounded-md px-3 py-2 text-xs" data-testid="client-ai-file-picker">
            <Upload size={14} className="text-violet-500" />
            {fileName || 'Upload an image or PDF (site photo, sketch, brief) — max 12 MB'}
          </label>
          <input id="client-ai-file-input" type="file" accept="image/*,application/pdf,.pdf" onChange={pickFile} className="hidden" data-testid="client-ai-file-input" />
          <Input value={instruction} onChange={(e) => setInstruction(e.target.value)} placeholder="Extra instruction (optional)" data-testid="client-ai-file-instruction" />
          {fileBase64 && fileMime === 'application/pdf' && (
            <div className="flex items-center gap-2 p-2 border border-stone-200 bg-stone-50 rounded text-xs text-stone-700">
              <span className="inline-flex items-center justify-center w-8 h-8 bg-red-100 text-red-700 rounded font-bold text-[10px]">PDF</span>
              {fileName}
            </div>
          )}
          {fileBase64 && fileMime !== 'application/pdf' && (
            <img src={fileBase64} alt="preview" className="max-h-32 rounded border border-stone-200" />
          )}
        </TabsContent>
      </Tabs>

      <div className="mt-3 flex justify-end">
        <Button onClick={runPreview} disabled={loading} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="client-ai-generate-btn">
          {loading ? <><Loader2 size={14} className="animate-spin mr-1" /> Generating…</> : <><Sparkles size={14} className="mr-1" /> Generate Estimate</>}
        </Button>
      </div>

      {previews.length > 0 && (
        <div className="mt-4 bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid="client-ai-results">
          <div className="flex items-center justify-between px-3 py-2 border-b border-stone-100 bg-stone-50">
            <div className="text-sm font-semibold text-stone-800">{previews.length} items · Estimated ₹{Math.round(totalEstimate).toLocaleString('en-IN')}</div>
            <Button size="sm" variant="outline" onClick={copySummary} data-testid="client-ai-copy-summary">
              <Copy size={12} className="mr-1" /> Copy summary
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase text-stone-500 bg-stone-50">
                <tr>
                  <th className="text-left px-3 py-2">Item</th>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Rate (₹)</th>
                  <th className="text-right px-3 py-2">Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                {previews.map((p, i) => (
                  <tr key={i} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`client-ai-row-${i}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-stone-900 text-xs">{p.task_title}</div>
                      {p.description && <div className="text-[11px] text-stone-500 mt-0.5 line-clamp-2">{p.description}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs text-stone-600">{p.main_category_name}{p.sub_category_name ? ` / ${p.sub_category_name}` : ''}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{p.quantity} {p.unit}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{Math.round(p.client_rate).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold text-stone-900">{Math.round(p.estimated_total).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-stone-400 px-3 py-2 italic">Estimates are indicative market rates. Actual quotations from your designer / contractor may vary.</p>
        </div>
      )}
    </div>
  );
}
